import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  FiFileText,
  FiUser,
  FiBriefcase,
  FiDownload,
  FiEdit,
  FiTrash2,
  FiUpload,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw,
  FiInfo,
  FiFolder
} from "react-icons/fi";
import {
  MdOutlinePendingActions,
  MdOutlineCheckCircleOutline,
  MdOutlineGavel,
  MdOutlineHourglassEmpty,
  MdOutlineEventAvailable,
  MdOutlineCancel
} from "react-icons/md";
import {
  RiFilePaperLine,
  RiCalendarCheckLine
} from "react-icons/ri";
import "../styles/UserCases.css";

/* ======================
   CASE STATUS CONFIGURATION
====================== */
const CASE_STATUS_CONFIG = {
  PENDING: {
    label: "Pending Review",
    icon: <MdOutlinePendingActions size={14} />,
    color: "#64748b",
    bgColor: "#f1f5f9",
    borderColor: "#cbd5e1"
  },
  FILED_IN_COURT: {
    label: "Filed in Court",
    icon: <RiFilePaperLine size={14} />,
    color: "#0ea5e9",
    bgColor: "#e0f2fe",
    borderColor: "#bae6fd"
  },
  HEARING: {
    label: "In Hearing",
    icon: <MdOutlineGavel size={14} />,
    color: "#2563eb",
    bgColor: "#dbeafe",
    borderColor: "#bfdbfe"
  },
  ADJOURNED: {
    label: "Adjourned",
    icon: <MdOutlineHourglassEmpty size={14} />,
    color: "#f59e0b",
    bgColor: "#fef3c7",
    borderColor: "#fde68a"
  },
  ALLOWED: {
    label: "Allowed",
    icon: <FiCheckCircle size={14} />,
    color: "#16a34a",
    bgColor: "#dcfce7",
    borderColor: "#bbf7d0"
  },
  DISMISSED: {
    label: "Dismissed",
    icon: <MdOutlineCancel size={14} />,
    color: "#dc2626",
    bgColor: "#fee2e2",
    borderColor: "#fecaca"
  },
  DISPOSED: {
    label: "Disposed",
    icon: <RiCalendarCheckLine size={14} />,
    color: "#7c3aed",
    bgColor: "#ede9fe",
    borderColor: "#ddd6fe"
  },
  CLOSED: {
    label: "Closed",
    icon: <FiCheckCircle size={14} />,
    color: "#0d9488",
    bgColor: "#ccfbf1",
    borderColor: "#99f6e4"
  },
  WITHDRAWN: {
    label: "Withdrawn",
    icon: <FiX size={14} />,
    color: "#6b7280",
    bgColor: "#f3f4f6",
    borderColor: "#e5e7eb"
  }
};

/* ======================
   DOCUMENT TYPES CONFIG
====================== */
const DOCUMENT_TYPES = [
  { value: "ID_PROOF", label: "ID Proof", icon: <FiUser size={12} /> },
  { value: "ADDRESS_PROOF", label: "Address Proof", icon: <FiInfo size={12} /> },
  { value: "AGREEMENT", label: "Agreement", icon: <FiFileText size={12} /> },
  { value: "FIR", label: "FIR Document", icon: <FiFileText size={12} /> },
  { value: "MEDICAL_REPORT", label: "Medical Report", icon: <FiFileText size={12} /> },
  { value: "BANK_STATEMENT", label: "Bank Statement", icon: <FiFileText size={12} /> },
  { value: "LEGAL_NOTICE", label: "Legal Notice", icon: <MdOutlineGavel size={12} /> },
  { value: "OTHER", label: "Other Document", icon: <FiFileText size={12} /> }
];

export default function UserCases() {
  const { token } = useAuth();
  const [cases, setCases] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [documentType, setDocumentType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editDocType, setEditDocType] = useState("");
  const [loading, setLoading] = useState(true);

  /* ======================
     FETCH CASES
  ====================== */
  const fetchCases = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/cases/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to fetch cases");

      const data = await res.json();
      setCases(data.cases || []);
    } catch (error) {
      console.error("Fetch cases error:", error);
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDetails = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setSelectedFiles([]);
    setDocumentType("");
  };

  /* ======================
     FILTER CASES BY TAB
  ====================== */
  const pendingCases = cases.filter(c =>
    ["PENDING", "FILED_IN_COURT", "HEARING", "ADJOURNED"].includes(c.caseStatus)
  );

  const completedCases = cases.filter(c =>
    ["ALLOWED", "DISMISSED", "DISPOSED", "CLOSED", "WITHDRAWN"].includes(c.caseStatus)
  );

  const visibleCases = activeTab === "PENDING" ? pendingCases : completedCases;

  /* ======================
     RENDER STATUS BADGE
  ====================== */
  const renderStatusBadge = (status) => {
    const config = CASE_STATUS_CONFIG[status] || {
      label: status,
      icon: <FiAlertCircle size={14} />,
      color: "#6b7280",
      bgColor: "#f3f4f6",
      borderColor: "#e5e7eb"
    };

    return (
      <span
        className="user-cases-status-badge"
        style={{
          backgroundColor: config.bgColor,
          color: config.color,
          border: `1px solid ${config.borderColor}`
        }}
      >
        {config.icon}
        <span>{config.label}</span>
      </span>
    );
  };

  // 🔒 Final safety check before upload
  const invalidFiles = selectedFiles.filter(
    (file) => file.type !== "application/pdf"
  );

  if (invalidFiles.length > 0) {
    toast.error("Only PDF files are allowed");
    return;
  }


  /* ======================
     UPLOAD DOCUMENTS
  ====================== */
  const uploadDocuments = async (caseId) => {
    if (!selectedFiles.length || !documentType) {
      toast.error("Please select document type and files");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      selectedFiles.forEach((file) =>
        formData.append("documents", file)
      );
      formData.append("documentType", documentType);

      const res = await fetch(`${API_BASE_URL}/api/cases/${caseId}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Upload failed");

      toast.success("Documents uploaded successfully");
      setSelectedFiles([]);
      setDocumentType("");
      fetchCases();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload documents");
    } finally {
      setUploading(false);
    }
  };

  /* ======================
     DELETE DOCUMENT
  ====================== */
  const deleteDocument = async (caseId, documentId, documentName) => {
    const result = await Swal.fire({
      title: "Delete Document?",
      html: `Are you sure you want to delete <strong>${documentName}</strong>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      width: "500px"
    });

    if (!result.isConfirmed) return;

    try {
      await fetch(
        `${API_BASE_URL}/api/cases/${caseId}/documents/${documentId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success("Document deleted successfully");
      fetchCases();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete document");
    }
  };

  /* ======================
     UPDATE DOCUMENT
  ====================== */
  const updateDocument = async () => {
    if (!editFile && !editDocType) {
      toast.error("Select file or document type to update");
      return;
    }

    try {
      const formData = new FormData();
      if (editFile) formData.append("document", editFile);
      if (editDocType) formData.append("documentType", editDocType);

      await fetch(
        `${API_BASE_URL}/api/cases/${editingDoc.caseId}/documents/${editingDoc.documentId}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      toast.success("Document updated successfully");
      setEditingDoc(null);
      setEditFile(null);
      setEditDocType("");
      fetchCases();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update document");
    }
  };

  /* ======================
     STATS CALCULATION
  ====================== */
  const stats = {
    pending: pendingCases.length,
    completed: completedCases.length,
    total: cases.length
  };

  return (
    <div className="premium-user-cases">
      {/* ======================
               HEADER
      ====================== */}
      <div className="user-cases-header">
        <div className="user-cases-header-left">
          <h1 className="user-cases-page-title">My Legal Cases</h1>
          <p className="user-cases-page-subtitle">
            Track and manage your legal cases and documents
          </p>
        </div>
        <div className="user-cases-header-right">
          <button
            className="user-cases-refresh-btn"
            onClick={fetchCases}
            disabled={loading}
          >
            <FiRefreshCw size={18} className={loading ? "spinning" : ""} />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {/* ======================
               STATS CARDS
      ====================== */}
      <div className="user-cases-stats-grid">
        <div className="user-cases-stat-card user-cases-stat-pending">
          <div className="user-cases-stat-icon">
            <MdOutlinePendingActions size={24} />
          </div>
          <div className="user-cases-stat-content">
            <h3 className="user-cases-stat-value">{stats.pending}</h3>
            <p className="user-cases-stat-label">Pending Cases</p>
            <span className="user-cases-stat-subtext">Awaiting Action</span>
          </div>
        </div>

        <div className="user-cases-stat-card user-cases-stat-completed">
          <div className="user-cases-stat-icon">
            <MdOutlineCheckCircleOutline size={24} />
          </div>
          <div className="user-cases-stat-content">
            <h3 className="user-cases-stat-value">{stats.completed}</h3>
            <p className="user-cases-stat-label">Completed Cases</p>
            <span className="user-cases-stat-subtext">Resolved Cases</span>
          </div>
        </div>

        <div className="user-cases-stat-card user-cases-stat-total">
          <div className="user-cases-stat-icon">
            <FiFolder size={24} />
          </div>
          <div className="user-cases-stat-content">
            <h3 className="user-cases-stat-value">{stats.total}</h3>
            <p className="user-cases-stat-label">Total Cases</p>
            <span className="user-cases-stat-subtext">All Legal Cases</span>
          </div>
        </div>
      </div>

      {/* ======================
               MAIN CONTENT
      ====================== */}
      <div className="user-cases-content">
        {/* Tabs */}
        <div className="user-cases-tabs-container">
          <div className="user-cases-main-tabs">
            <button
              className={`user-cases-tab-btn ${activeTab === "PENDING" ? "user-cases-tab-active" : ""}`}
              onClick={() => {
                setActiveTab("PENDING");
                setExpandedId(null);
              }}
            >
              <MdOutlinePendingActions size={16} />
              <span>Pending ({stats.pending})</span>
            </button>

            <button
              className={`user-cases-tab-btn ${activeTab === "COMPLETED" ? "user-cases-tab-active" : ""}`}
              onClick={() => {
                setActiveTab("COMPLETED");
                setExpandedId(null);
              }}
            >
              <MdOutlineCheckCircleOutline size={16} />
              <span>Completed ({stats.completed})</span>
            </button>
          </div>
        </div>

        {/* Cases List */}
        <div className="user-cases-list-container">
          {loading ? (
            <div className="user-cases-loading-state">
              <div className="user-cases-loading-spinner"></div>
              <p>Loading cases...</p>
            </div>
          ) : visibleCases.length === 0 ? (
            <div className="user-cases-empty-state">
              <MdOutlineEventAvailable size={48} />
              <p>No cases found</p>
              <small>Try selecting a different tab or check back later</small>
            </div>
          ) : (
            <div className="user-cases-cards-grid">
              {visibleCases.map((c) => (
                <div key={c._id} className="user-cases-card">
                  {/* Card Header */}
                  <div className="user-cases-card-header">
                    <div className="user-cases-case-info">
                      <div className="user-cases-case-avatar">
                        <FiFileText size={20} />
                      </div>
                      <div className="user-cases-case-details">
                        <div className="user-cases-case-id">
                          Case: {c.caseId}
                        </div>
                        <div className="user-cases-solicitor-name">
                          <FiUser size={12} />
                          <span>{c.solicitorDetails?.name || "No Solicitor Assigned"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="user-cases-status-container">
                      {renderStatusBadge(c.caseStatus)}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="user-cases-card-body">
                    <div className="user-cases-meta-info">
                      <div className="user-cases-category">
                        <FiBriefcase size={14} />
                        <span>{c.practiceCategory || "General"}</span>
                      </div>
                      <div className="user-cases-area">
                        <FiBriefcase size={14} />
                        <span>{c.practiceArea || "Not Specified"}</span>
                      </div>
                    </div>

                    <div className="user-cases-type">
                      <FiFileText size={14} />
                      <span>{c.caseType || "Consultation"}</span>
                    </div>

                    <div className="user-cases-description-preview">
                      {c.caseDescription?.substring(0, 100) || "No description provided..."}
                      {c.caseDescription?.length > 100 && "..."}
                    </div>

                    <div className="user-cases-actions">
                      <button
                        className="user-cases-view-details-btn"
                        onClick={() => toggleDetails(c._id)}
                      >
                        {expandedId === c._id ? "Hide Details" : "View Details"}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === c._id && (
                    <div className="user-cases-expanded-details">
                      {/* Case Details */}
                      <div className="user-cases-details-section">
                        <h4>Case Details</h4>
                        <div className="user-cases-details-grid">
                          <div className="user-cases-detail-item">
                            <strong>Case ID:</strong>
                            <span>{c.caseId}</span>
                          </div>
                          <div className="user-cases-detail-item">
                            <strong>Solicitor:</strong>
                            <span>{c.solicitorDetails?.name || "Not assigned"}</span>
                          </div>
                          <div className="user-cases-detail-item">
                            <strong>Practice Category:</strong>
                            <span>{c.practiceCategory || "Not specified"}</span>
                          </div>
                          <div className="user-cases-detail-item">
                            <strong>Practice Area:</strong>
                            <span>{c.practiceArea || "Not specified"}</span>
                          </div>
                          <div className="user-cases-detail-item">
                            <strong>Case Type:</strong>
                            <span>{c.caseType || "Not specified"}</span>
                          </div>
                          <div className="user-cases-detail-item">
                            <strong>Created:</strong>
                            <span>{new Date(c.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Full Description */}
                      <div className="user-cases-description-section">
                        <h4>Case Description</h4>
                        <div className="user-cases-description-content">
                          {c.caseDescription || "No description provided."}
                        </div>
                      </div>

                      {/* Documents Section */}
                      <div className="user-cases-documents-section">
                        <h4>
                          <FiFolder size={16} />
                          Case Documents
                        </h4>

                        {(!c.documents || c.documents.length === 0) ? (
                          <div className="user-cases-no-documents">
                            <FiFileText size={24} />
                            <p>No documents uploaded</p>
                            <small>Upload documents to proceed with your case</small>
                          </div>
                        ) : (
                          <div className="user-cases-documents-list">
                            {c.documents.map((doc) => (
                              <div key={doc._id} className="user-cases-document-item">
                                <div className="user-cases-document-info">
                                  <div className="user-cases-document-type">
                                    {DOCUMENT_TYPES.find(d => d.value === doc.documentType)?.icon || <FiFileText size={14} />}
                                    <span>{doc.documentType.replace(/_/g, ' ')}</span>
                                  </div>
                                </div>
                                <div className="user-cases-document-actions">
                                  <a
                                    href={doc.blobUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="user-cases-document-view-btn"
                                  >
                                    <FiDownload size={14} />
                                    <span>Download</span>
                                  </a>
                                  <button
                                    className="user-cases-document-edit-btn"
                                    onClick={() =>
                                      setEditingDoc({
                                        caseId: c._id,
                                        documentId: doc._id,
                                        documentName: doc.documentType
                                      })
                                    }
                                  >
                                    <FiEdit size={14} />
                                  </button>
                                  <button
                                    className="user-cases-document-delete-btn"
                                    onClick={() =>
                                      deleteDocument(c._id, doc._id, doc.documentType)
                                    }
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Upload Section */}
                        <div className="user-cases-upload-section">
                          <h5>Upload New Documents</h5>
                          <div className="user-cases-upload-controls">
                            <select
                              className="user-cases-doc-type-select"
                              value={documentType}
                              onChange={(e) => setDocumentType(e.target.value)}
                            >
                              <option value="">Select Document Type</option>
                              {DOCUMENT_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>

                            <div className="user-cases-file-upload">
                              <label className="user-cases-file-input-label">
                                <FiUpload size={16} />
                                <span>Choose Files</span>
                                <input
                                  type="file"
                                  multiple
                                  accept="application/pdf"
                                  onChange={(e) => {
                                    const files = Array.from(e.target.files);

                                    // 🔒 Allow only PDFs
                                    const invalidFiles = files.filter(
                                      (file) => file.type !== "application/pdf"
                                    );

                                    if (invalidFiles.length > 0) {
                                      toast.error("Only PDF files are allowed");
                                      e.target.value = "";        // reset input
                                      setSelectedFiles([]);
                                      return;
                                    }

                                    setSelectedFiles(files);
                                  }}
                                  className="user-cases-file-input"
                                />

                                <small className="user-cases-file-hint">
                                  Only PDF files are allowed (Max 10 MB per file)
                                </small>

                              </label>
                              {selectedFiles.length > 0 && (
                                <span className="user-cases-file-count">
                                  {selectedFiles.length} file(s) selected
                                </span>
                              )}
                            </div>

                            <button
                              className="user-cases-upload-btn"
                              onClick={() => uploadDocuments(c._id)}
                              disabled={uploading || !selectedFiles.length || !documentType}
                            >
                              {uploading ? (
                                <>
                                  <FiRefreshCw size={16} className="spinning" />
                                  <span>Uploading...</span>
                                </>
                              ) : (
                                <>
                                  <FiUpload size={16} />
                                  <span>Upload Documents</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Document Modal */}
      {editingDoc && (
        <div className="user-cases-modal-overlay">
          <div className="user-cases-modal">
            <div className="user-cases-modal-header">
              <h3>Edit Document</h3>
              <button
                className="user-cases-modal-close"
                onClick={() => {
                  setEditingDoc(null);
                  setEditFile(null);
                  setEditDocType("");
                }}
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="user-cases-modal-body">
              <div className="user-cases-modal-form">
                <div className="user-cases-form-group">
                  <label>Document Type</label>
                  <select
                    className="user-cases-form-select"
                    value={editDocType}
                    onChange={(e) => setEditDocType(e.target.value)}
                  >
                    <option value="">Keep current type</option>
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="user-cases-form-group">
                  <label>Replace File</label>
                  <div className="user-cases-file-upload-modal">
                    <label className="user-cases-file-input-label-modal">
                      <FiUpload size={16} />
                      <span>Choose New File</span>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];

                          if (!file) return;

                          if (file.type !== "application/pdf") {
                            toast.error("Only PDF files are allowed");
                            e.target.value = "";
                            setEditFile(null);
                            return;
                          }

                          setEditFile(file);
                        }}
                        className="user-cases-file-input"
                      />

                    </label>
                    <small className="user-cases-file-hint">
                      Only PDF files are allowed (Max 10 MB per file)
                    </small>
                    {editFile && (
                      <span className="user-cases-file-name">
                        Selected: {editFile.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="user-cases-modal-footer">
              <button
                className="user-cases-modal-cancel"
                onClick={() => {
                  setEditingDoc(null);
                  setEditFile(null);
                  setEditDocType("");
                }}
              >
                Cancel
              </button>
              <button
                className="user-cases-modal-confirm"
                onClick={updateDocument}
                disabled={!editFile && !editDocType}
              >
                Update Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}